import { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, LogOut } from 'lucide-react';
import clsx from 'clsx';
import { Avatar, StatusPill, NotificationBell } from '../components';
import { useAuth } from '../lib/auth';
import { useT, type TranslationKey } from '../i18n';
import logoZr from '../assets/logo-zr.svg';
import type { Role } from '../types/api';
import styles from './TopBar.module.css';

interface NavItem {
  to: string;
  labelKey: TranslationKey;
  /** Visible only to these roles. Undefined = visible to all. */
  roles?: Role[];
}

/** Module tabs — role visibility moved verbatim from the old SideNav.
 * Порядок: Обращения — рабочее место менеджера, поэтому первый пункт;
 * дашборд — аналитика, уходит в конец меню (после /orders). */
const NAV_ITEMS: NavItem[] = [
  { to: '/inquiries',   labelKey: 'nav.inquiries' },
  { to: '/clients',     labelKey: 'nav.clients' },
  { to: '/integrators', labelKey: 'nav.integrators',  roles: ['MANUFACTURER'] },
  { to: '/bookings',    labelKey: 'nav.bookings' },
  { to: '/catalog',     labelKey: 'nav.catalog' },
  { to: '/orders',      labelKey: 'nav.orders' },
  { to: '/dashboard',   labelKey: 'nav.dashboard' },
];

/**
 * Board chrome: single top bar — brand, horizontal module tabs (pill style,
 * active tab cobalt), user area with role pill + avatar menu.
 */
export function TopBar() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onClickAway = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [menuOpen]);

  if (!user) return null;

  const roleLabel =
    user.role === 'INTEGRATOR'
      ? `${t('roles.integrator')}${user.integrator?.name ? ` — ${user.integrator.name}` : ''}`
      : t('roles.manufacturer');

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.roles || item.roles.includes(user.role),
  );

  return (
    <header className={styles.bar}>
      <div className={styles.brand}>
        <img src={logoZr} alt="Завод роботов" className={styles.brandLogo} />
        <span className={styles.brandDivider} aria-hidden="true" />
        <span className={styles.brandTitle}>CRM</span>
      </div>

      <nav className={styles.tabs} aria-label={t('nav.primary')}>
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              clsx(styles.tab, isActive && styles.tabActive)
            }
          >
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>

      <div className={styles.userArea} ref={menuRef}>
        <NotificationBell />
        <StatusPill
          tone={user.role === 'MANUFACTURER' ? 'info' : 'neutral'}
          className={styles.rolePill}
        >
          {roleLabel}
        </StatusPill>
        <button
          type="button"
          className={styles.userBtn}
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <Avatar name={user.name} size="sm" />
          <span className={styles.userName}>{user.name}</span>
          <ChevronDown size={14} aria-hidden="true" />
        </button>
        {menuOpen && (
          <div className={styles.menu} role="menu">
            <div className={styles.menuHeader}>
              <div className={styles.menuName}>{user.name}</div>
              <div className={styles.menuEmail}>{user.email}</div>
            </div>
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
            >
              <LogOut size={14} />
              <span>{t('auth.signOut')}</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
